// ---------------------------------------------------------------------------
// Email Express preset templates — shared structural source of truth.
//
// A preset template fixes an email's LAYOUT: the ordered list of sections,
// which sections are locked in place vs. reorderable, which sections carry an
// AI-generated image slot, and what per-section copy fields exist. Colors and
// fonts are NEVER part of a template — they resolve from the selected brand at
// render time via `resolveEmailBrandStyle` (shared/email-brand-style.ts), so
// every template is brand-abstracted: the references define color ROLES
// ("dark contrast band", "accent CTA"), not literal hexes.
//
// Preset emails are a SEPARATE render path from archetype-driven emails:
//  - blocks array stores the template's section instance ids (ids == types),
//  - per-section copy lives under blockCopy.instances[<id>].preset,
//  - AI image slots live on the email's `presetImages` jsonb array,
//  - presets NEVER enter the AI polish pipeline (deterministic render only),
//  - "no preset" (presetTemplateKey null) keeps today's behavior byte-for-byte.
//
// The DB registry (`email_preset_templates`) is seeded from these definitions
// at startup and adds only durable asset URLs (R2 reference image, thumbnail,
// shared texture) on top of this structural definition.
// ---------------------------------------------------------------------------

import { emailEditedBlockKey } from "./schema";

// Task #1144: the five never-shipped stub templates (myth_bust, sale_event,
// product_spotlight, comparison_reviews, awareness_month) were removed — only
// the block-driven Perfect Pair Bundle survives. Removed keys now fail
// isEmailPresetTemplateKey, so both selection routes reject them with 400.
export const EMAIL_PRESET_TEMPLATE_KEYS = [
  "bundle_pair", // Perfect Pair Bundle (block-driven, RETIRED — legacy rows only)
  // The five V2-parity templates (block-driven). Sequences mirror Email
  // Express V2's preset sequences, mapped onto V1 standard blocks.
  "classic_promo",
  "discount_drop",
  "visual_story",
  "product_spotlight",
  "minimal_announcement",
] as const;
export type EmailPresetTemplateKey = (typeof EMAIL_PRESET_TEMPLATE_KEYS)[number];
export function isEmailPresetTemplateKey(x: unknown): x is EmailPresetTemplateKey {
  return (
    typeof x === "string" &&
    (EMAIL_PRESET_TEMPLATE_KEYS as readonly string[]).includes(x)
  );
}

// Section render primitives. Each is a deterministic client-side renderer in
// client/src/lib/email-preset-blocks.tsx. Section INSTANCE ids equal their type
// (each type appears at most once per template), so `emailBlockType` is the
// identity for preset sections and the existing instance-id plumbing
// (blockCopyStatus, blockCopy.instances, lockedBlocks) applies unchanged.
export const EMAIL_PRESET_SECTION_TYPES = [
  "preset_header", // logo header (brand logo / wordmark)
  "preset_hero", // per-template hero (myth/truth banners, sale poster, ...)
  "preset_fact_band", // dark-contrast fact band (brand primary surface)
  "preset_body", // editorial body / intro / education paragraphs
  "preset_section_header", // benefit line band with emphasized word
  "preset_ingredient", // dark ingredient-spotlight band (macro image + CTA)
  "preset_benefits", // benefit-ribbon stack (headline + product + 4 ribbons)
  "preset_offer_lockup", // code + shipping line + CTA lockup band
  "preset_category_grid", // shop-by-category alternating rows
  "preset_checklist", // checklist band (header + product photo + 4 items)
  "preset_reviews", // customer review cards (quote + stars + name + CTA)
  "preset_comparison_band", // dark villain-vs-hero band with ribbons
  "preset_stat_band", // dark stat/infographic band (icon row + stat copy)
  "preset_transition", // italic transition-quote band
  "preset_product_reveal", // product reveal (photo + headline + lines + CTA)
  "preset_closing", // short closing + social icons
  "preset_about", // about-brand close (from brand doctrine)
  "preset_trust", // trust-badge footer strip
] as const;
export type EmailPresetSectionType = (typeof EMAIL_PRESET_SECTION_TYPES)[number];

// Per-section copy payload, stored at blockCopy.instances[<sectionId>].preset.
// One loose interface shared by every section type — each type reads only the
// fields it uses (documented inline). Emphasis convention: the copy generator
// wraps emphasized words/phrases in **double asterisks** (rendered bold /
// highlighted) and _underscores_ (rendered italic); the renderer parses these.
export interface EmailPresetSectionCopy {
  // preset_hero: T1 myth/truth banner lines.
  mythLine?: string;
  truthLine?: string;
  // preset_hero: T2 sale poster.
  eventName?: string;
  offerLine?: string;
  codeBadge?: string;
  // preset_hero: T3 two stacked phrases; T6 headline+subline; T4/T5 headline.
  phrases?: string[];
  headline?: string;
  subline?: string;
  // CTA label used by any section that renders a button.
  ctaLabel?: string;
  // preset_fact_band / preset_stat_band / preset_transition: single copy line.
  text?: string;
  // preset_body: editorial paragraphs (+ optional italic pull line and
  // closing line).
  paragraphs?: string[];
  pullLine?: string;
  closingLine?: string;
  // preset_ingredient: ingredient name + explainer.
  ingredientName?: string;
  explainer?: string;
  // preset_benefits / preset_comparison_band: exactly 4 short ribbons.
  ribbons?: string[];
  // preset_comparison_band: villain vs hero lines.
  villainLine?: string;
  heroLine?: string;
  // preset_offer_lockup.
  leadLine?: string;
  code?: string;
  shippingLine?: string;
  // preset_category_grid: 4–6 rows, real brand photos only (never AI).
  categories?: { label: string; imageUrl?: string; url?: string }[];
  // preset_checklist: header highlight + exactly 4 items.
  highlightWord?: string;
  items?: string[];
  // preset_reviews: exactly 2 cards, REAL reviews only (placeholder when none).
  reviewCards?: { quote: string; name: string; rating: number }[];
  // preset_about: aboutText ≤90w from brand doctrine.
  aboutText?: string;
  // preset_product_reveal: two short lines under the headline.
  revealLines?: string[];
  // preset_trust: badge labels (fixed defaults, user-editable, never AI).
  badges?: { label: string }[];
}

export type EmailPresetSectionDef = {
  // Section instance id AND type (ids == types for preset sections).
  key: EmailPresetSectionType;
  label: string;
  // Locked sections are pinned to their defined position (header/hero at the
  // top, about/trust/closing/reveal etc. at the bottom per template); unlocked
  // sections are reorderable within the middle band via the existing drag UI.
  locked: boolean;
  // AI-regenerable copy (every preset section except header/trust).
  regenerable: boolean;
  // This section renders an AI-generated image slot (keyed by section id on
  // the email's presetImages array). Absent → the section uses real product /
  // brand photos or no image at all.
  aiImage?: boolean;
};

export type EmailPresetInputDef = {
  key: string; // field on EmailPresetInputs
  label: string;
  type: "text" | "textarea" | "product";
  required: boolean;
  placeholder?: string;
  help?: string;
};

// Per-preset data persisted on the email's `preset_data` jsonb. Templates no
// longer collect extra picker inputs — everything is derived from the standard
// settings (brand, product, persona, narrative angle, sale). These fields
// remain for LEGACY emails created while the picker had per-template inputs;
// when present, factual values (awareness stats) stay PASS-THROUGH ONLY: the
// copy generator never invents or alters them.
export interface EmailPresetInputs {
  mythToBust?: string; // T1 legacy (now derived from the narrative angle)
  villain?: string; // T5 legacy (derived from the narrative angle)
  awarenessName?: string; // T6 legacy (derived from angle + current month)
  awarenessMonth?: string; // T6 legacy
  awarenessFacts?: string[]; // T6 legacy (verbatim pass-through when present)
  secondProductId?: string; // T4 legacy snapshot
  secondProductName?: string; // T4 legacy snapshot
  secondProductImageUrl?: string | null; // T4 legacy snapshot
  // Task #1032 — block-driven mode marker. A `true` value means this email was
  // generated through the BLOCK-DRIVEN path (standard hero/body/footer +
  // user-checked optional blocks, standard copy pipeline, Perfect Pair
  // renderer) rather than the legacy preset-sections path. Legacy preset
  // emails (and legacy bundle_pair rows) never carry it.
  blockDriven?: boolean;
}

// One AI image slot on the email's `preset_images` jsonb array.
export interface EmailPresetImage {
  instanceId: string; // the section id this image belongs to
  prompt: string | null;
  status: "pending" | "ready" | "failed";
  url: string | null;
  error: string | null;
  startedAt: string | null;
}

export type EmailPresetTemplateDef = {
  key: EmailPresetTemplateKey;
  name: string;
  description: string;
  useCase: string;
  // Rollout gate: templates start disabled and are flipped on one-by-one as
  // each design is fully built out. Disabled templates still render for
  // EXISTING emails (legacy rows keep working) but cannot be picked for new
  // emails — enforced in the pickers AND at both server selection points.
  enabled: boolean;
  // Task #1032 — block-driven templates generate through the STANDARD block
  // pipeline (hero/body/footer + user-checked optional blocks, standard copy
  // worker) and render through a dedicated template renderer variant. They are
  // selectable in the CAMPAIGN picker even while `enabled` stays false
  // (`enabled` keeps gating the legacy preset-sections path + flow
  // select-template, which this mode deliberately does not support).
  blockDriven?: boolean;
  // Retired templates can no longer be picked for NEW emails (hidden from the
  // registry route, refused by the generate route) but EXISTING emails created
  // with them keep rendering/editing normally.
  retired?: boolean;
  // Block-driven templates with a FIXED block sequence: the ordered standard
  // block list (mandatory + optionals) this template always generates with.
  // The picker pre-checks these optionals; the generate route unions them into
  // the selection. Absent → legacy checkbox-only behavior (bundle_pair).
  blockDrivenBlocks?: readonly string[];
  // Per-template layout-variant overrides layered OVER the shared
  // EMAIL_BLOCK_DRIVEN_VARIANTS map (see emailBlockDrivenVariantsFor).
  blockDrivenVariants?: Record<string, string>;
  // Seed-time only: the attached_assets reference filename mirrored to R2 by
  // the startup seed. Never read at render time.
  referenceAssetFile: string;
  sections: EmailPresetSectionDef[];
  // Extra create-form inputs this template needs (beyond brand/persona/product).
  inputs: EmailPresetInputDef[];
  // Human-readable copy contract (word limits etc.) — the single reference the
  // server copy generator prompts/clamps from and docs cite.
  copySpec: string;
};

const S = (
  key: EmailPresetSectionType,
  label: string,
  opts?: { locked?: boolean; regenerable?: boolean; aiImage?: boolean },
): EmailPresetSectionDef => ({
  key,
  label,
  locked: opts?.locked ?? false,
  regenerable: opts?.regenerable ?? true,
  ...(opts?.aiImage ? { aiImage: true } : {}),
});

export const EMAIL_PRESET_TEMPLATES: EmailPresetTemplateDef[] = [
  {
    key: "bundle_pair",
    name: "Perfect Pair Bundle",
    description:
      "Two products that belong together: pairing hero, editorial body, a checklist band and two real customer review cards.",
    useCase: "Sell a two-product bundle or natural product pairing.",
    // BLOCK-DRIVEN (Task #1032): selectable in the campaign picker, generates
    // via the standard block pipeline + Perfect Pair renderer variant.
    // `enabled` stays false so the legacy preset-sections path and the flow
    // select-template route keep refusing it.
    enabled: false,
    blockDriven: true,
    // RETIRED in favor of the V2-parity templates below: hidden from pickers,
    // refused for new emails, existing emails keep rendering.
    retired: true,
    referenceAssetFile:
      "20260204_Product_Highlight__Perfect_Pair_Bundle_1783535021091.png",
    sections: [
      S("preset_header", "Logo header", { locked: true, regenerable: false }),
      S("preset_hero", "Pairing hero", { locked: true, aiImage: true }),
      S("preset_body", "Editorial body"),
      S("preset_checklist", "Checklist band"),
      S("preset_reviews", "Customer reviews"),
      S("preset_about", "About the brand", { locked: true }),
      S("preset_trust", "Trust badges", { locked: true, regenerable: false }),
    ],
    // The pairing partner comes from the selected product itself (a bundle) or
    // the copy pairs it with a complementary ritual — no extra picker inputs.
    inputs: [],
    copySpec:
      "hero headline ≤7w; 3 paragraphs ≤35w **bolding exact product names**; checklist header ≤4w with one **highlighted** word + exactly 4 items ≤4w from brand doctrine; exactly 2 review cards {quote ≤60w, reviewerName, rating} from REAL reviews (editable placeholder when none exist — never fabricated); aboutBrand ≤90w.",
  },
  // -------------------------------------------------------------------------
  // V2-parity templates. Each maps an Email Express V2 preset sequence onto V1
  // standard blocks (V2 logo header → logo_banner, lifestyle+headline → hero,
  // product callout → selling_points; V1's "Body copy" block keeps its name
  // and place as the editorial text section). All are block-driven: standard
  // copy pipeline, standard renderer, per-template pinned layout variants.
  // `sections`/`inputs` stay empty — the legacy preset-sections path never
  // applies to them. `referenceAssetFile` names are intentionally absent from
  // the tracked reference fixture directory, so the seed logs a warning and
  // seeds without images.
  // -------------------------------------------------------------------------
  {
    key: "classic_promo",
    name: "Classic Promo",
    description:
      "Logo banner, hero, body copy, a full-width product photo, selling points and a closing CTA band.",
    useCase: "The all-purpose promo email — lead with the product story.",
    enabled: false,
    blockDriven: true,
    referenceAssetFile: "email-template-classic-promo.png",
    blockDrivenBlocks: [
      "logo_banner",
      "hero",
      "body",
      "image_only",
      "selling_points",
      "cta",
      "footer",
    ],
    blockDrivenVariants: {
      hero: "split",
      body: "standard",
      logo_banner: "centered-minimal",
      selling_points: "checklist-product",
    },
    sections: [],
    inputs: [],
    copySpec:
      "hero headline ≤8w; 2 body paragraphs ≤35w; 4 selling points ≤6w; CTA band headline ≤6w + button label ≤3w.",
  },
  {
    key: "discount_drop",
    name: "Discount Drop",
    description:
      "Offer-forward: hero, body copy, an image + discount-code band, selling points and a CTA band.",
    useCase: "Announce a sale or discount code.",
    enabled: false,
    blockDriven: true,
    referenceAssetFile: "email-template-discount-drop.png",
    blockDrivenBlocks: [
      "logo_banner",
      "hero",
      "body",
      "image_discount",
      "selling_points",
      "cta",
      "footer",
    ],
    blockDrivenVariants: {
      hero: "image-overlay",
      body: "standard",
      logo_banner: "color-band",
      selling_points: "icon-grid",
    },
    sections: [],
    inputs: [],
    copySpec:
      "hero headline ≤8w leading with the offer; discount band note ≤12w + the sale code verbatim; 4 selling points ≤6w; CTA band headline ≤6w.",
  },
  {
    key: "visual_story",
    name: "Visual Story",
    description:
      "Image-led: full-bleed hero, editorial body copy, a full-width product photo and a CTA band.",
    useCase: "Brand storytelling with minimal copy.",
    enabled: false,
    blockDriven: true,
    referenceAssetFile: "email-template-visual-story.png",
    blockDrivenBlocks: [
      "logo_banner",
      "hero",
      "body",
      "image_only",
      "cta",
      "footer",
    ],
    blockDrivenVariants: {
      hero: "full-bleed-minimal",
      body: "editorial-margins",
      logo_banner: "centered-minimal",
    },
    sections: [],
    inputs: [],
    copySpec:
      "hero headline ≤6w; 2 short editorial paragraphs ≤30w; CTA band headline ≤6w + button label ≤3w.",
  },
  {
    key: "product_spotlight",
    name: "Product Spotlight",
    description:
      "Product-first: hero, body copy, an image + discount band, selling points and a CTA band.",
    useCase: "Deep-dive one product's benefits.",
    enabled: false,
    blockDriven: true,
    referenceAssetFile: "email-template-product-spotlight.png",
    blockDrivenBlocks: [
      "logo_banner",
      "hero",
      "body",
      "image_discount",
      "selling_points",
      "cta",
      "footer",
    ],
    blockDrivenVariants: {
      hero: "split",
      body: "standard",
      logo_banner: "editorial-type",
      selling_points: "annotated-lifestyle",
    },
    sections: [],
    inputs: [],
    copySpec:
      "hero headline ≤8w naming the product; 2 body paragraphs ≤35w on benefits; 4 selling points ≤6w; CTA band headline ≤6w.",
  },
  {
    key: "minimal_announcement",
    name: "Minimal Announcement",
    description: "Short and typographic: hero, body copy and a CTA band.",
    useCase: "A quick announcement or launch note.",
    enabled: false,
    blockDriven: true,
    referenceAssetFile: "email-template-minimal-announcement.png",
    blockDrivenBlocks: ["logo_banner", "hero", "body", "cta", "footer"],
    blockDrivenVariants: {
      // Every template hero carries an image (user direction Jul 29 2026) —
      // the announcement hero overlays the headline on the hero photo.
      hero: "image-overlay",
      body: "standard",
      logo_banner: "centered-minimal",
    },
    sections: [],
    inputs: [],
    copySpec:
      "hero headline ≤7w; 1–2 short paragraphs ≤30w; CTA band headline ≤6w + button label ≤3w.",
  },
];

export function getEmailPresetTemplate(
  key: string | null | undefined,
): EmailPresetTemplateDef | null {
  if (!key) return null;
  return EMAIL_PRESET_TEMPLATES.find((t) => t.key === key) ?? null;
}

// Whether a template can be picked for NEW emails. Existing emails created
// with a since-disabled template keep rendering/editing normally — this gate
// only applies at selection time (create picker + flow select-template).
export function isEmailPresetTemplateEnabled(
  key: string | null | undefined,
): boolean {
  return getEmailPresetTemplate(key)?.enabled === true;
}

// ---------------------------------------------------------------------------
// Block-driven template mode (Task #1032). A block-driven template email uses
// the STANDARD Email Express blocks (hero/body/footer + checked optionals),
// the standard copy pipeline, and a dedicated renderer variant — the preset
// key only drives layout/structure patterns. Marked by
// presetData.blockDriven === true so legacy preset emails (including legacy
// bundle_pair rows from the preset-sections era) are never re-interpreted.
// ---------------------------------------------------------------------------

// Whether a template KEY generates through the block-driven path for NEW
// emails (campaign picker + generate route).
export function isEmailBlockDrivenTemplate(
  key: string | null | undefined,
): boolean {
  return getEmailPresetTemplate(key)?.blockDriven === true;
}

// Whether an EXISTING email row is a block-driven template email. Requires
// BOTH the template key and the persisted presetData marker — a legacy
// bundle_pair email without the marker stays on the preset-sections path.
export function isEmailBlockDrivenPresetEmail(email: {
  presetTemplateKey?: string | null;
  presetData?: unknown;
}): boolean {
  if (!email?.presetTemplateKey) return false;
  if (!isEmailBlockDrivenTemplate(email.presetTemplateKey)) return false;
  const data = email.presetData as EmailPresetInputs | null | undefined;
  return !!data && typeof data === "object" && data.blockDriven === true;
}

// Fixed render/generation order for block-driven emails. Regardless of the
// order blocks were checked, the email always lays out in this sequence.
export const EMAIL_BLOCK_DRIVEN_MANDATORY = ["hero", "body", "footer"] as const;
export const EMAIL_BLOCK_DRIVEN_ORDER = [
  "logo_banner",
  "hero",
  "body",
  "image_only",
  "image_discount",
  "selling_points",
  "us_vs_them",
  "comparison_table",
  "did_you_know",
  "reviews",
  "product_list",
  "cta",
  "footer",
] as const;

// Normalize a selected-blocks list for a block-driven email: mandatory blocks
// are always included, unknown keys are dropped, duplicates collapse, and the
// result is in EMAIL_BLOCK_DRIVEN_ORDER regardless of input order.
// A block-driven template's own OPTIONAL block list (its fixed sequence minus
// the mandatory blocks). Null when the template doesn't pin a sequence.
export function emailBlockDrivenTemplateOptionals(
  key: string | null | undefined,
): string[] | null {
  const def = getEmailPresetTemplate(key);
  if (!def?.blockDriven || !def.blockDrivenBlocks) return null;
  const mandatory = new Set<string>(EMAIL_BLOCK_DRIVEN_MANDATORY);
  return def.blockDrivenBlocks.filter((b) => !mandatory.has(b));
}

// The pinned layout variants for a block-driven email: the shared map layered
// under any per-template overrides.
export function emailBlockDrivenVariantsFor(
  key: string | null | undefined,
): Record<string, string> {
  const def = getEmailPresetTemplate(key);
  return {
    ...EMAIL_BLOCK_DRIVEN_VARIANTS,
    ...(def?.blockDrivenVariants ?? {}),
  };
}

// ---------------------------------------------------------------------------
// Body-copy spec (Task #1959). The Sim Body Copy workflow writes ~3 paragraphs
// unless told otherwise, so the template's copySpec must be threaded into the
// call for short templates (e.g. Minimal Announcement) to actually come out
// short. Block-driven preset emails use their template's copySpec verbatim;
// everything else (freeform campaigns/flows) gets this default, which mirrors
// the historical templated-body shape (2–3 short paragraphs).
// ---------------------------------------------------------------------------
export const EMAIL_BODY_COPY_DEFAULT_SPEC =
  "body copy: 2–3 short, distinct paragraphs, each ≤45 words.";

// Resolve the body-copy spec for an email: the block-driven preset template's
// copySpec when `presetTemplateKey` names one, else the default spec. Pass
// null/undefined (or a non-block-driven key) to get the default.
export function emailBodyCopySpecFor(
  presetTemplateKey: string | null | undefined,
): string {
  const def = getEmailPresetTemplate(presetTemplateKey);
  if (def?.blockDriven && def.copySpec?.trim()) return def.copySpec.trim();
  return EMAIL_BODY_COPY_DEFAULT_SPEC;
}

// Normalize a block-driven email's stored blocks PRESERVING the user's order
// (drag-to-reorder support): unknown keys and duplicates drop, missing
// mandatory blocks are inserted at their canonical template positions, and the
// footer stays pinned last. Falls back to canonical order semantics when the
// stored list is empty.
export function normalizeEmailBlockDrivenBlocksPreserving(
  selected?: readonly string[] | null,
): string[] {
  const known = new Set<string>(EMAIL_BLOCK_DRIVEN_ORDER);
  // Repeated-instance support (Task #2286): the editor can add EXTRA image
  // blocks as "<type>__<n>" instance ids. An id is valid when its base TYPE
  // is a known block-driven type; ordering ranks by that base type so extra
  // instances slot next to their siblings while the fixed order holds.
  const baseType = (k: string) => k.split("__")[0];
  const rank = (k: string) =>
    EMAIL_BLOCK_DRIVEN_ORDER.indexOf(
      baseType(k) as (typeof EMAIL_BLOCK_DRIVEN_ORDER)[number],
    );
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of Array.isArray(selected) ? selected : []) {
    if (typeof x !== "string" || !known.has(baseType(x)) || seen.has(x)) continue;
    seen.add(x);
    if (x === "footer") continue; // pinned last, re-appended below
    out.push(x);
  }
  for (const m of EMAIL_BLOCK_DRIVEN_MANDATORY) {
    if (m === "footer" || seen.has(m)) continue;
    const at = out.findIndex((k) => rank(k) > rank(m));
    out.splice(at < 0 ? out.length : at, 0, m);
  }
  out.push("footer");
  return out;
}

export function orderEmailBlockDrivenBlocks(
  selected?: readonly string[] | null,
): string[] {
  const want = new Set<string>(EMAIL_BLOCK_DRIVEN_MANDATORY);
  for (const x of Array.isArray(selected) ? selected : []) {
    if (typeof x === "string") want.add(x);
  }
  return EMAIL_BLOCK_DRIVEN_ORDER.filter((k) => want.has(k));
}

// Fixed layout variants for the Perfect Pair structural patterns (Task #1032):
// hero = full-bleed photo with the headline/CTA rendered OVER the image
// (matching the reference template's hero); selling points = headline +
// supporting product image + tight bullet checklist; reviews = stacked
// testimonial-card rhythm; did-you-know = a short paragraph band; the rest
// stay on their calmest catalog entries. Assigned at generation (overriding
// the rhythmic assignment) so the standard image/copy pipeline and every
// render surface agree on structure. The layout is FIXED for block-driven
// emails: the editor hides layout cycling and the server refuses
// layout-swapping regen modes for them.
export const EMAIL_BLOCK_DRIVEN_VARIANTS: Record<string, string> = {
  image_only: "standard",
  image_discount: "standard",
  cta: "standard",
  hero: "image-overlay",
  body: "standard",
  logo_banner: "centered-minimal",
  selling_points: "checklist-product",
  us_vs_them: "table-minimal",
  comparison_table: "grid-classic",
  did_you_know: "color-band",
  reviews: "ugc-stack",
  product_list: "grid-2col",
};

// Default trust-badge strip (preset_trust). Fixed per brand — user-editable in
// the editor, never AI-generated.
export const EMAIL_PRESET_DEFAULT_BADGES: { label: string }[] = [
  { label: "Secure checkout" },
  { label: "Satisfaction guaranteed" },
  { label: "Fast shipping" },
];

// Normalize a preset email's stored blocks to display order. Locked sections
// are pinned to their template-defined positions; unlocked (middle) sections
// keep the user's stored order. Unknown/duplicate ids are dropped, missing
// sections are restored at their template position — so a template change or
// bad write can never lose a section or unlock a pinned one.
export function normalizeEmailPresetBlocks(
  def: EmailPresetTemplateDef,
  raw?: string[] | null,
): string[] {
  const defKeys = def.sections.map((s) => s.key as string);
  const lockedSet = new Set(
    def.sections.filter((s) => s.locked).map((s) => s.key as string),
  );
  // The user's stored order for UNLOCKED sections only.
  const storedUnlocked: string[] = [];
  const seen = new Set<string>();
  for (const x of Array.isArray(raw) ? raw : []) {
    if (typeof x !== "string" || seen.has(x)) continue;
    if (!defKeys.includes(x) || lockedSet.has(x)) continue;
    seen.add(x);
    storedUnlocked.push(x);
  }
  // Append any unlocked sections missing from the stored order, in def order.
  for (const k of defKeys) {
    if (!lockedSet.has(k) && !seen.has(k)) {
      seen.add(k);
      storedUnlocked.push(k);
    }
  }
  // Rebuild: locked keys at their template positions, unlocked slots filled in
  // the user's order.
  let u = 0;
  return defKeys.map((k) => (lockedSet.has(k) ? k : storedUnlocked[u++]));
}

// The visible render order for a preset email (hidden sections removed; locked
// sections are never hideable).
export function visibleEmailPresetBlocks(
  def: EmailPresetTemplateDef,
  blocks?: string[] | null,
  hidden?: string[] | null,
): string[] {
  const lockedSet = new Set(
    def.sections.filter((s) => s.locked).map((s) => s.key as string),
  );
  const hiddenSet = new Set(
    (Array.isArray(hidden) ? hidden : []).filter(
      (x) => typeof x === "string" && !lockedSet.has(x),
    ),
  );
  return normalizeEmailPresetBlocks(def, blocks).filter((k) => !hiddenSet.has(k));
}

// AI image slots for a template (section ids that carry an aiImage slot).
export function emailPresetAiImageSlots(def: EmailPresetTemplateDef): string[] {
  return def.sections.filter((s) => s.aiImage).map((s) => s.key as string);
}

// ---------------------------------------------------------------------------
// Regeneration planning (server + tests). Mirrors the non-preset semantics:
// - Explicit "Regenerate this section" targets ONLY that section. It is
//   refused when the section is user-LOCKED (explicit "don't touch"); a
//   hand-edit does NOT refuse it (targeting the section IS the intent).
// - Whole-email "Regenerate content" (no block) targets every regenerable
//   section EXCEPT those the user locked or hand-edited (block:<id> in
//   userEditedFields) — those are preserved as-is.
// The returned blockCopyStatus flips ONLY the targets to "pending"; every
// other section keeps its existing status, so the copy worker (which merges
// generated copy for PENDING sections only) never overwrites a non-target.
// ---------------------------------------------------------------------------
export type EmailPresetRegenPlan =
  | { ok: false; reason: "invalid_block" | "locked" }
  | { ok: true; targets: string[]; blockCopyStatus: Record<string, string> };

export function planEmailPresetRegen(
  def: EmailPresetTemplateDef,
  opts: {
    block?: string | null;
    lockedBlocks?: string[] | null;
    userEditedFields?: string[] | null;
    existingStatus?: Record<string, string> | null;
  },
): EmailPresetRegenPlan {
  const lockedSet = new Set(
    Array.isArray(opts.lockedBlocks) ? opts.lockedBlocks : [],
  );
  const editedSet = new Set(
    Array.isArray(opts.userEditedFields) ? opts.userEditedFields : [],
  );
  const regenerable = def.sections.filter((s) => s.regenerable);
  let targets: string[];
  if (opts.block) {
    const sec = regenerable.find((s) => (s.key as string) === opts.block);
    if (!sec) return { ok: false, reason: "invalid_block" };
    if (lockedSet.has(opts.block)) return { ok: false, reason: "locked" };
    targets = [opts.block];
  } else {
    targets = regenerable
      .map((s) => s.key as string)
      .filter(
        (k) => !lockedSet.has(k) && !editedSet.has(emailEditedBlockKey(k)),
      );
  }
  const blockCopyStatus: Record<string, string> = {
    ...(opts.existingStatus ?? {}),
  };
  for (const k of targets) blockCopyStatus[k] = "pending";
  return { ok: true, targets, blockCopyStatus };
}

// The copy worker's view of the plan: which sections of this preset email are
// currently PENDING (i.e. were targeted by create/regenerate). Falls back to
// every regenerable section when nothing is pending (defensive — a legacy row
// mid-flight during deploy).
export function emailPresetPendingTargets(
  def: EmailPresetTemplateDef,
  blockCopyStatus: Record<string, string> | null | undefined,
): string[] {
  const regenerable = def.sections
    .filter((s) => s.regenerable)
    .map((s) => s.key as string);
  const pending = regenerable.filter(
    (k) => (blockCopyStatus ?? {})[k] === "pending",
  );
  return pending.length ? pending : regenerable;
}

// Registry row shape shared with the client (GET /api/email-express/preset-templates).
export interface EmailPresetTemplateListItem {
  key: EmailPresetTemplateKey;
  name: string;
  description: string;
  useCase: string;
  referenceImageUrl: string | null;
  thumbnailUrl: string | null;
  assets: { grainUrl?: string | null } | null;
  inputs: EmailPresetInputDef[];
}
